import { Button, DataTable, DynamicContainer, FormSection } from '@hai/ui-react';
import { HaiDataTableColumnType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import { observer } from 'mobx-react';
import { isEmpty, isNil } from 'ramda';
import React, { MutableRefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSwitch } from 'src/js/component/base/form/form-switch';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useMutable } from 'src/js/hook/use-mutable';
import { createListStore, ListSelector } from 'src/js/util/listSelector';
import { v4 as uuidv4 } from 'uuid';

import { MetadataCoTRelayModal } from './metadata-cot-relay-modal';
import { MetadataCoTRelay, MetadataViewModel, getTOSHint, getTTLHint, newMetadataCoTRelay } from './metadata-models';
import { validateMetadataHostname } from './metadata-store';
import { useStateWatch } from 'src/js/store/use-watch';

interface Props {
  values: MetadataViewModel;
  formRef: MutableRefObject<FormikProps<MetadataViewModel>>;
}

export const MetadataCotRelay: React.FunctionComponent<Props> = observer(({ values, formRef }) => {
  const { t } = useTranslation();

  let listController: ListSelector<MetadataCoTRelay> = null;
  const [uuid] = useMutable(uuidv4());
  listController = createListStore(values.relays, uuid());

  const [addShown, showAdd, hideAdd] = useModalControls();
  const [editShown, showEdit, hideEdit] = useModalControls();
  const [cotValues, setCoTValues] = useMutable<MetadataCoTRelay>(undefined);
  const handleAdd = (value: MetadataCoTRelay) => {
    validateMetadataHostname(value.ip, t).then((res) => {
      if (res?.data?.success) {
        // Replace existing relay
        const relays = [...formRef.current.values.relays];
        if (!isNil(value.index)) {
          relays.splice(value.index, 1, value);
        } else {
          relays.splice(0, 0, value);
        }
        listController.updateList(relays);
        formRef.current.setFieldValue('relays', relays, true);
        formRef.current.setFieldTouched('relays');
        hideAdd();
        hideEdit();
      }
    });
  };

  const handleDelete = () => {
    const relays = [...formRef.current.values.relays];
    for (let i = relays.length - 1; i >= 0; i--) {
      if (listController.isSelected(relays[i])) {
        relays.splice(i, 1);
      }
    }
    listController.updateList(relays);
    listController.unselectAll();
    formRef.current.setFieldValue('relays', relays, true);
    formRef.current.setFieldTouched('relays');
  };

  const bulkActionComponents = () => {
    return (
      <Button state="idle" size="small" onClick={handleDelete}>
        {t('general.deleteBtn')}
      </Button>
    );
  };

  const handleAction = (eventKey: string, data: any) => {
    switch (eventKey) {
      case 'edit': {
        const relay = formRef.current.values.relays[data.index];
        setCoTValues({ ...relay, index: data.index });
        showEdit();
        break;
      }

      case 'delete':
        if (data.index >= 0 && data.index < listController.list.length) {
          const toDelete = listController.list[data.index];
          listController.unselectAll();
          listController.select(toDelete);
          handleDelete();
        }
        break;
      default:
        break;
    }
  };

  const [selectAll, setSelectAll] = useState(false);
  const handleCheckAll = (checked: boolean) => {
    setSelectAll(checked);
    checked ? listController.selectAll() : listController.unselectAll();
  };

  const selectedMatrix = useStateWatch(listController, 'rowMatrix', listController.rowMatrix);

  return (
    <>
      <FormSection className="hai-mb-6" title={t('metadata.settings.cotRelaying')}>
        <DynamicContainer minColumns={2} maxColumns={2}>
          <FormSwitch className="hai-mb-4" name="enableCotRelay" switchLabel={t('metadata.settings.cotRelaying')} />
          {values.enableCotRelay && (
            <div className="buttonRow">
              <Button
                size="small"
                onClick={() => {
                  setCoTValues(newMetadataCoTRelay());
                  showAdd();
                }}
                disabled={values.relays.length >= 8}
              >
                {t('general.add')}
              </Button>
            </div>
          )}
        </DynamicContainer>
        {values.enableCotRelay && (
          <DataTable
            selectable={true}
            sortable={false}
            columnStructure={[
              { fieldKey: 'ip', useFsMask: true, title: t('metadata.settings.address') },
              { fieldKey: 'port', useFsMask: true, title: t('metadata.settings.port') },
              { fieldKey: 'ttl', title: t('metadata.settings.ttl') },
              { fieldKey: 'tos', title: t('metadata.settings.tos') },
              { fieldKey: '', title: '', type: HaiDataTableColumnType.ACTIONS },
            ]}
            onCheckAll={handleCheckAll}
            checkAll={selectAll}
            className={`titlebar-lite ${isEmpty(listController.list) ? 'hai-mb-6' : ''}`}
            datatableContainerClassName="datatable-formsection"
          >
            <DataTable.Header bulkActions={bulkActionComponents} />
            {listController.list.map((value: MetadataCoTRelay, index: number) => (
              <DataTable.Row
                key={`cot-${index}`}
                rowData={{
                  index: index,
                  id: `${value.ip}-${index}`,
                  ip: value.ip,
                  port: value.port,
                  ttl: getTTLHint(value.ttl, values.type, false, t),
                  tos: getTOSHint(value.tos, values.type, false, t),
                }}
                onSelect={() => listController.toggleRow(value)}
                checked={selectedMatrix[index]} // TODO add a unit test to cover a new item inserted and shifting of indexes
                actionItems={[
                  {
                    actionIcon: 'Edit',
                    title: t('general.edit'),
                    eventKey: 'edit',
                    onSelect: handleAction,
                  },
                  {
                    actionIcon: 'TrashCan',
                    title: t('general.delete'),
                    eventKey: 'delete',
                    onSelect: handleAction,
                  },
                ]}
              ></DataTable.Row>
            ))}
          </DataTable>
        )}
      </FormSection>
      {addShown && (
        <MetadataCoTRelayModal
          onCancel={hideAdd}
          onApply={handleAdd}
          type={values.type}
          data={cotValues()}
          edit={false}
        />
      )}
      {editShown && (
        <MetadataCoTRelayModal
          onCancel={hideEdit}
          onApply={handleAdd}
          type={values.type}
          data={cotValues()}
          edit={true}
        />
      )}
    </>
  );
});
